import { isErrorEvent, isLogEvent, processEnvelope } from "./parser-BnBZzLYl.js";

//#region src/format/event.ts
const LANGUAGE_EXTENSIONS = {
	".java": "java",
	".py": "python",
	".js": "javascript",
	".jsx": "javascript",
	".ts": "javascript",
	".tsx": "javascript",
	".rb": "ruby",
	".php": "php"
};
const LANGUAGE_MODULE_PATTERNS = [[/^(java\.|com\.|org\.)/, "java"]];
/**
* Detects the programming language of a stack frame based on the file extension.
* Falls back to the platform parameter if no filename is available or extension is unrecognized.
*
* @param frame - The stack frame containing file and location information
* @param platform - Optional platform hint to use as fallback
* @returns The detected language or platform fallback or "unknown"
*/
function detectLanguage(frame, platform) {
	if (frame.filename) {
		const ext = frame.filename.toLowerCase().match(/\.[^.]+$/)?.[0];
		if (ext && LANGUAGE_EXTENSIONS[ext]) return LANGUAGE_EXTENSIONS[ext];
	}
	if (frame.module) {
		for (const [pattern, language] of LANGUAGE_MODULE_PATTERNS) if (pattern.test(frame.module)) return language;
	}
	return platform || "unknown";
}
/**
* Formats a stack frame into a language-specific string representation.
* Different languages have different conventions for displaying stack traces.
*
* @param frame - The stack frame to format
* @param frameIndex - Optional frame index for languages that display frame numbers
* @param platform - Optional platform hint for language detection fallback
* @returns Formatted stack frame string
*/
function formatFrameHeader(frame, frameIndex, platform) {
	const language = detectLanguage(frame, platform);
	switch (language) {
		case "java": {
			const className = frame.module || "UnknownClass";
			const method = frame.function || "<unknown>";
			const source = frame.filename || "Unknown Source";
			const location = frame.lineNo ? `:${frame.lineNo}` : "";
			return `at ${className}.${method}(${source}${location})`;
		}
		case "python": {
			const file = frame.filename || frame.absPath || frame.module || "<unknown>";
			const func = frame.function || "<module>";
			const line = frame.lineNo ? `, line ${frame.lineNo}` : "";
			return `  File "${file}"${line}, in ${func}`;
		}
		case "javascript": return `${[
			frame.filename,
			frame.lineNo,
			frame.colNo
		].filter((i) => !!i).join(":")}${frame.function ? ` (${frame.function})` : ""}`;
		case "ruby": {
			const file = frame.filename || frame.module || "<unknown>";
			const func = frame.function ? ` \`${frame.function}\`` : "";
			const line = frame.lineNo ? `:${frame.lineNo}:in` : "";
			return `    from ${file}${line}${func}`;
		}
		case "php": {
			const file = frame.filename || "<unknown>";
			const line = frame.lineNo ? `(${frame.lineNo})` : "";
			const func = frame.function || "<unknown>";
			const prefix = frameIndex !== void 0 ? `#${frameIndex} ` : "";
			return `${prefix}${file}${line}: ${func}()`;
		}
		default: {
			const func = frame.function || "<unknown>";
			const location = frame.filename || frame.module || "<unknown>";
			const line = frame.lineNo ? `:${frame.lineNo}` : "";
			const col = frame.colNo != null ? `:${frame.colNo}` : "";
			return `    at ${func} (${location}${line}${col})`;
		}
	}
}
/**
* Formats a Sentry event into a structured markdown output.
* Includes error messages, stack traces, request info, and contextual data.
*
* @param event - The Sentry event to format
* @returns Formatted markdown string
*/
function formatEventOutput(event) {
	let output = "";
	const messageEntry = event.entries.find((e) => e.type === "message");
	const exceptionEntry = event.entries.find((e) => e.type === "exception");
	const threadsEntry = event.entries.find((e) => e.type === "threads");
	const requestEntry = event.entries.find((e) => e.type === "request");
	if (messageEntry) output += formatMessageInterfaceOutput(event, messageEntry.data);
	if (exceptionEntry) output += formatExceptionInterfaceOutput(event, exceptionEntry.data);
	else if (threadsEntry) output += formatThreadsInterfaceOutput(event, threadsEntry.data);
	if (requestEntry) output += formatRequestInterfaceOutput(event, requestEntry.data);
	output += formatTags(event.tags);
	output += formatContexts(event.contexts);
	return output;
}
/**
* Extracts the context line matching the frame's line number for inline display.
* This is used in the full stacktrace view to show the actual line of code
* that caused the error inline with the stack frame.
*
* @param frame - The stack frame containing context lines
* @returns The line of code at the frame's line number, or empty string if not available
*/
function renderInlineContext(frame) {
	if (!frame.context?.length || !frame.lineNo) return "";
	const contextLine = frame.context.find(([lineNo]) => lineNo === frame.lineNo);
	return contextLine ? `\n${contextLine[1]}` : "";
}
/**
* Renders an enhanced view of a stack frame with context lines and variables.
* Used for the "Most Relevant Frame" section to provide detailed information
* about the most relevant application frame where the error occurred.
*
* @param frame - The stack frame to render with enhanced information
* @param event - The Sentry event containing platform information for language detection
* @returns Formatted string with frame header, context lines, and variables table
*/
function renderEnhancedFrame(frame, event) {
	const parts = [];
	parts.push("**Most Relevant Frame:**");
	parts.push("─────────────────────");
	parts.push(formatFrameHeader(frame, void 0, event.platform));
	if (frame.context?.length) {
		const contextLines = renderContextLines(frame);
		if (contextLines) {
			parts.push("");
			parts.push(contextLines);
		}
	}
	if (frame.vars && Object.keys(frame.vars).length > 0) {
		parts.push("");
		parts.push(renderVariablesTable(frame.vars));
	}
	return parts.join("\n");
}
function formatExceptionInterfaceOutput(event, data) {
	const parts = [];
	const exceptions = data.values || (data.value ? [data.value] : []);
	if (exceptions.length === 0) return "";
	const isChained = exceptions.length > 1;
	[...exceptions].reverse().forEach((exception, index) => {
		if (!exception) return;
		if (isChained && index > 0) {
			parts.push("");
			parts.push(getExceptionChainMessage(event.platform || null, index, exceptions.length));
			parts.push("");
		}
		const exceptionTitle = `${exception.type}${exception.value ? `: ${exception.value}` : ""}`;
		parts.push(index === 0 ? "### Error" : `### ${exceptionTitle}`);
		parts.push("");
		if (index === 0) {
			parts.push("```");
			parts.push(exceptionTitle);
			parts.push("```");
			parts.push("");
		}
		if (!exception.stacktrace || !exception.stacktrace.frames) {
			parts.push("**Stacktrace:**");
			parts.push("```");
			parts.push("No stacktrace available");
			parts.push("```");
			return;
		}
		const frames = exception.stacktrace.frames;
		if (index === 0) {
			const firstInAppFrame = findFirstInAppFrame(frames);
			if (firstInAppFrame && (firstInAppFrame.context?.length || firstInAppFrame.vars)) {
				parts.push(renderEnhancedFrame(firstInAppFrame, event));
				parts.push("");
				parts.push("**Full Stacktrace:**");
				parts.push("────────────────");
			} else parts.push("**Stacktrace:**");
		} else parts.push("**Stacktrace:**");
		parts.push("```");
		parts.push(frames.map((frame) => {
			const header = formatFrameHeader(frame, void 0, event.platform);
			const context = renderInlineContext(frame);
			return `${header}${context}`;
		}).join("\n"));
		parts.push("```");
	});
	parts.push("");
	parts.push("");
	return parts.join("\n");
}
/**
* Get the appropriate exception chain message based on the platform
*/
function getExceptionChainMessage(platform, index, _totalExceptions) {
	const defaultMessage = "**During handling of the above exception, another exception occurred:**";
	if (!platform) return defaultMessage;
	switch (platform.toLowerCase()) {
		case "python": return "**During handling of the above exception, another exception occurred:**";
		case "java": return "**Caused by:**";
		case "csharp":
		case "dotnet": return "**---> Inner Exception:**";
		case "ruby": return "**Caused by:**";
		case "go": return "**Wrapped error:**";
		case "rust": return `**Caused by (${index}):**`;
		default: return defaultMessage;
	}
}
function formatRequestInterfaceOutput(_event, data) {
	if (!data.method || !data.url) return "";
	return `### HTTP Request\n\n**Method:** ${data.method}\n**URL:** ${data.url}\n\n`;
}
function formatMessageInterfaceOutput(_event, data) {
	if (!data.formatted && !data.message) return "";
	const message = data.formatted || data.message || "";
	return `### Error\n\n\`\`\`\n${message}\n\`\`\`\n\n`;
}
function formatThreadsInterfaceOutput(event, data) {
	if (!data.values || data.values.length === 0) return "";
	const crashedThread = data.values.find((t) => t.crashed);
	if (!crashedThread?.stacktrace?.frames) return "";
	const parts = [];
	if (crashedThread.name) {
		parts.push(`**Thread** (${crashedThread.name})`);
		parts.push("");
	}
	const frames = crashedThread.stacktrace.frames;
	const firstInAppFrame = findFirstInAppFrame(frames);
	if (firstInAppFrame && (firstInAppFrame.context?.length || firstInAppFrame.vars)) {
		parts.push(renderEnhancedFrame(firstInAppFrame, event));
		parts.push("");
		parts.push("**Full Stacktrace:**");
		parts.push("────────────────");
	} else parts.push("**Stacktrace:**");
	parts.push("```");
	parts.push(frames.map((frame) => {
		const header = formatFrameHeader(frame, void 0, event.platform);
		const context = renderInlineContext(frame);
		return `${header}${context}`;
	}).join("\n"));
	parts.push("```");
	parts.push("");
	return parts.join("\n");
}
/**
* Renders surrounding source code context for a stack frame.
* Shows a window of code lines around the error line with visual indicators.
*
* @param frame - The stack frame containing context lines
* @param contextSize - Number of lines to show before and after the error line (default: 3)
* @returns Formatted context lines with line numbers and arrow indicator for the error line
*/
function renderContextLines(frame, contextSize = 3) {
	if (!frame.context || frame.context.length === 0 || !frame.lineNo) return "";
	const lines = [];
	const errorLine = frame.lineNo;
	const maxLineNoWidth = Math.max(...frame.context.map(([lineNo]) => lineNo.toString().length));
	for (const [lineNo, code] of frame.context) {
		const isErrorLine = lineNo === errorLine;
		const lineNoStr = lineNo.toString().padStart(maxLineNoWidth, " ");
		if (Math.abs(lineNo - errorLine) <= contextSize) if (isErrorLine) lines.push(`  → ${lineNoStr} │ ${code}`);
		else lines.push(`    ${lineNoStr} │ ${code}`);
	}
	return lines.join("\n");
}
/**
* Formats a variable value for display in the variables table.
* Handles different types appropriately and safely, converting complex objects
* to readable representations and handling edge cases like circular references.
*
* @param value - The variable value to format (can be any type)
* @param maxLength - Maximum length for stringified objects/arrays (default: 80)
* @returns Human-readable string representation of the value
*/
function formatVariableValue(value, maxLength = 80) {
	try {
		if (typeof value === "string") return `"${value}"`;
		if (value === null) return "null";
		if (value === void 0) return "undefined";
		if (typeof value === "object") {
			const stringified = JSON.stringify(value);
			if (stringified.length > maxLength) {
				const truncateAt = maxLength - 6;
				let truncated = stringified.substring(0, truncateAt);
				const lastComma = truncated.lastIndexOf(",");
				if (lastComma > 0) truncated = truncated.substring(0, lastComma);
				if (Array.isArray(value)) return `${truncated}, ...]`;
				return `${truncated}, ...}`;
			}
			return stringified;
		}
		return String(value);
	} catch {
		return `<${typeof value}>`;
	}
}
/**
* Renders a table of local variables in a tree-like format.
* Uses box-drawing characters to create a visual hierarchy of variables
* and their values at the point where the error occurred.
*
* @param vars - Object containing variable names as keys and their values
* @returns Formatted variables table with tree-style prefix characters
*/
function renderVariablesTable(vars) {
	const entries = Object.entries(vars);
	if (entries.length === 0) return "";
	const lines = ["Local Variables:"];
	const lastIndex = entries.length - 1;
	entries.forEach(([key, value], index) => {
		const prefix = index === lastIndex ? "└─" : "├─";
		const valueStr = formatVariableValue(value);
		lines.push(`${prefix} ${key}: ${valueStr}`);
	});
	return lines.join("\n");
}
/**
* Finds the first application frame (in_app) in a stack trace.
* Searches from the bottom of the stack (oldest frame) to find the first
* frame that belongs to the user's application code rather than libraries.
*
* @param frames - Array of stack frames, typically in reverse chronological order
* @returns The first in-app frame found, or undefined if none exist
*/
function findFirstInAppFrame(frames) {
	for (let i = frames.length - 1; i >= 0; i--) if (frames[i].inApp === true) return frames[i];
	return void 0;
}
function formatTags(tags) {
	if (!tags || tags.length === 0) return "";
	return `### Tags\n\n${tags.map((tag) => `**${tag.key}**: ${tag.value}`).join("\n")}\n\n`;
}
function formatContexts(contexts) {
	if (!contexts || Object.keys(contexts).length === 0) return "";
	return `### Additional Context\n\nThese are additional context provided by the user when they're instrumenting their application.\n\n${Object.entries(contexts).map(([name, data]) => `**${name}**\n${Object.entries(data).filter(([key, _]) => key !== "type").map(([key, value]) => {
		return `${key}: ${JSON.stringify(value, void 0, 2)}`;
	}).join("\n")}`).join("\n\n")}\n\n`;
}

//#endregion
//#region src/format/errors.ts
async function formatErrorEnvelope(container) {
	const processedEnvelope = container.getParsedEnvelope();
	const { event: [, items] } = processedEnvelope;
	const formatted = [];
	for (const item of items) {
		const [{ type }, payload] = item;
		if (type === "event" && isErrorEvent(payload)) formatted.push(formatEventOutput(processErrorEvent(payload)));
	}
	return formatted;
}
function processErrorEvent(event) {
	const entries = [];
	if (event.exception) entries.push({
		type: "exception",
		data: event.exception
	});
	if (event.request) entries.push({
		type: "request",
		data: event.request
	});
	if (event.breadcrumbs) entries.push({
		type: "breadcrumbs",
		data: event.breadcrumbs
	});
	if (event.spans) entries.push({
		type: "spans",
		data: event.spans
	});
	if (event.threads) entries.push({
		type: "threads",
		data: event.threads
	});
	return {
		message: event.message ?? "",
		id: event.event_id ?? "",
		type: "error",
		tags: Object.entries(event.tags ?? {}).map(([key, value]) => ({
			key,
			value: String(value)
		})),
		dateCreated: event.timestamp ? new Date(event.timestamp).toISOString() : (/* @__PURE__ */ new Date()).toISOString(),
		title: event.message ?? "",
		entries,
		contexts: event.contexts,
		platform: event.platform
	};
}

//#endregion
//#region src/format/logs.ts
async function formatLogEnvelope(container) {
	const parsedEnvelope = container.getParsedEnvelope();
	const { event: [, items] } = parsedEnvelope;
	const formatted = [];
	for (const item of items) {
		const [{ type }, payload] = item;
		if (type === "log" && isLogEvent(payload)) for (const log of payload.items) formatted.push(processLogEvent(log));
	}
	return formatted;
}
function processLogEvent(event) {
	let attr = "";
	for (const [key, property] of Object.entries(event.attributes ?? {})) {
		if (key.startsWith("sentry.")) continue;
		attr += `${key}: ${property.value} (${property.type})\n`;
	}
	return `${(/* @__PURE__ */ new Date(event.timestamp * 1e3)).toISOString()} ${event.level} ${event.body}
Attributes:
${attr}`;
}

//#endregion
//#region src/format/traces.ts
/**
* Extract trace events from envelopes and group by trace ID
*/
function extractTracesFromEnvelopes(containers) {
	const traces = /* @__PURE__ */ new Map();
	for (const container of containers) try {
		const events = extractTraceEventsFromContainer(container);
		for (const event of events) {
			if (!event.trace_context?.trace_id) continue;
			const traceId = event.trace_context.trace_id;
			if (!traces.has(traceId)) traces.set(traceId, {
				trace_id: traceId,
				span_count: 0,
				error_count: 0,
				events: []
			});
			const trace = traces.get(traceId);
			trace.events.push(event);
			if (event.type === "error") trace.error_count++;
			if (event.spans) trace.span_count += event.spans.length;
			if (event.transaction && !trace.root_transaction) trace.root_transaction = event.transaction;
			const eventStartTime = event.start_timestamp || event.timestamp;
			if (eventStartTime) {
				if (!trace.start_timestamp || eventStartTime < trace.start_timestamp) trace.start_timestamp = eventStartTime;
			}
		}
	} catch (err) {
		console.error("Error extracting trace events:", err);
	}
	for (const trace of traces.values()) calculateTraceDuration(trace);
	return traces;
}
/**
* Extract trace-related events from a single envelope container
*/
function extractTraceEventsFromContainer(container) {
	const events = [];
	try {
		const parsed = processEnvelope({
			contentType: container.getContentType(),
			data: container.getData()
		});
		const [, items] = parsed.event;
		for (const item of items) {
			const [{ type }, payload] = item;
			if (type === "event" && payload && typeof payload === "object") {
				const event = payload;
				if (event.contexts?.trace?.trace_id) {
					let eventType = "unknown";
					if (event.exception || event.level === "error") eventType = "error";
					else if (event.type) eventType = event.type;
					events.push({
						event_id: event.event_id || "",
						type: eventType,
						timestamp: event.timestamp,
						start_timestamp: event.start_timestamp,
						transaction: event.transaction,
						trace_context: event.contexts.trace,
						spans: event.spans,
						level: event.level,
						platform: event.platform,
						message: event.message,
						exception: event.exception
					});
				}
			}
			if (type === "transaction" && payload && typeof payload === "object") {
				const transaction = payload;
				if (transaction.contexts?.trace?.trace_id) events.push({
					event_id: transaction.event_id || "",
					type: "transaction",
					timestamp: transaction.timestamp,
					start_timestamp: transaction.start_timestamp,
					transaction: transaction.transaction,
					trace_context: transaction.contexts.trace,
					spans: transaction.spans,
					level: transaction.level,
					platform: transaction.platform
				});
			}
		}
	} catch (err) {
		console.error("Error parsing envelope for traces:", err);
	}
	return events;
}
/**
* Calculate trace duration from start to latest event
*/
function calculateTraceDuration(trace) {
	if (!trace.start_timestamp) return;
	let latestTimestamp = trace.start_timestamp;
	for (const event of trace.events) {
		if (event.timestamp && event.timestamp > latestTimestamp) latestTimestamp = event.timestamp;
		if (event.spans) {
			for (const span of event.spans) if (span.timestamp && span.timestamp > latestTimestamp) latestTimestamp = span.timestamp;
		}
	}
	trace.duration = (latestTimestamp - trace.start_timestamp) * 1e3;
}
function buildSpanTree(trace) {
	const allSpans = [];
	const spanMap = /* @__PURE__ */ new Map();
	for (const event of trace.events) {
		if (event.trace_context) {
			const eventSpan = {
				span_id: event.trace_context.span_id,
				parent_span_id: event.trace_context.parent_span_id,
				op: event.type === "transaction" ? "transaction" : event.type,
				description: event.transaction || event.message || "unnamed",
				is_transaction: event.type === "transaction" || !!event.transaction,
				children: [],
				level: 0,
				event_id: event.event_id,
				duration: event.timestamp && event.start_timestamp ? (event.timestamp - event.start_timestamp) * 1e3 : void 0
			};
			allSpans.push(eventSpan);
			spanMap.set(eventSpan.span_id, eventSpan);
		}
		if (event.spans && Array.isArray(event.spans)) for (const span of event.spans) {
			if (spanMap.has(span.span_id)) continue;
			const spanNode = {
				span_id: span.span_id,
				parent_span_id: span.parent_span_id || event.trace_context?.span_id,
				op: span.op,
				description: span.description || "unnamed",
				duration: span.duration !== void 0 ? span.duration : span.timestamp && span.start_timestamp ? (span.timestamp - span.start_timestamp) * 1e3 : void 0,
				status: span.status,
				children: [],
				level: 0
			};
			allSpans.push(spanNode);
			spanMap.set(spanNode.span_id, spanNode);
		}
	}
	const rootSpans = [];
	const orphanedByParentId = /* @__PURE__ */ new Map();
	for (const span of allSpans) if (!span.parent_span_id) rootSpans.push(span);
	else if (spanMap.has(span.parent_span_id)) {
		const parent = spanMap.get(span.parent_span_id);
		parent.children.push(span);
		span.level = parent.level + 1;
	} else {
		if (!orphanedByParentId.has(span.parent_span_id)) orphanedByParentId.set(span.parent_span_id, []);
		orphanedByParentId.get(span.parent_span_id).push(span);
	}
	function updateLevels(node, newLevel) {
		node.level = newLevel;
		for (const child of node.children) updateLevels(child, newLevel + 1);
	}
	for (const [parentId, orphans] of orphanedByParentId) {
		const orphanParent = {
			span_id: parentId,
			op: "orphan",
			description: "missing or unknown parent span",
			children: orphans,
			level: 0,
			is_transaction: false
		};
		const parentRoot = rootSpans.length === 1 ? rootSpans[0] : null;
		if (parentRoot) {
			parentRoot.children.push(orphanParent);
			updateLevels(orphanParent, parentRoot.level + 1);
		} else {
			rootSpans.push(orphanParent);
			updateLevels(orphanParent, 0);
		}
	}
	for (const span of allSpans) span.children.sort((a, b) => {
		if (a.duration !== void 0 && b.duration !== void 0) return b.duration - a.duration;
		return 0;
	});
	if (rootSpans.length > 1) {
		const syntheticRoot = {
			span_id: trace.trace_id.substring(0, 16),
			description: `Trace ${trace.trace_id.substring(0, 8)}`,
			op: "trace",
			is_transaction: false,
			children: rootSpans.sort((a, b) => (b.duration || 0) - (a.duration || 0)),
			level: 0,
			duration: trace.duration
		};
		for (const root of rootSpans) updateLevels(root, 1);
		return [syntheticRoot];
	}
	return rootSpans;
}
/**
* Format trace summary for display
*/
function formatTraceSummary(trace) {
	const duration = trace.duration ? `${Math.round(trace.duration)}ms` : "unknown";
	const transaction = trace.root_transaction || "unnamed";
	const timestamp = trace.start_timestamp ? (/* @__PURE__ */ new Date(trace.start_timestamp * 1e3)).toISOString() : "unknown";
	return `**${trace.trace_id.substring(0, 8)}** | ${transaction} | ${duration} | ${trace.span_count} spans | ${trace.error_count} errors | ${timestamp}`;
}
/**
* Render span tree as hierarchical text
*/
function renderSpanTree(spans) {
	const lines = [];
	function formatSpanDisplayName(span) {
		if (span.is_transaction) return span.description || "unnamed transaction";
		return span.description || span.op || "unnamed";
	}
	function renderSpan(span, prefix = "", isLast = true) {
		const shortId = span.span_id.substring(0, 8);
		const connector = prefix === "" ? "" : isLast ? "└─ " : "├─ ";
		const displayName = formatSpanDisplayName(span);
		if (span.is_transaction) {
			const duration = span.duration ? `${Math.round(span.duration)}ms` : "unknown";
			lines.push(`${prefix}${connector}${displayName} [${shortId} · ${duration}]`);
		} else {
			const duration = span.duration ? `${Math.round(span.duration)}ms` : "unknown";
			const opDisplay = span.op && span.op !== "default" && span.op !== "unknown" && span.op !== "transaction" ? ` · ${span.op}` : "";
			lines.push(`${prefix}${connector}${displayName} [${shortId}${opDisplay} · ${duration}]`);
		}
		for (let i = 0; i < span.children.length; i++) {
			const child = span.children[i];
			const isLastChild = i === span.children.length - 1;
			const childPrefix = prefix + (isLast ? "   " : "│  ");
			renderSpan(child, childPrefix, isLastChild);
		}
	}
	for (let i = 0; i < spans.length; i++) {
		const span = spans[i];
		const isLastRoot = i === spans.length - 1;
		renderSpan(span, "", isLastRoot);
	}
	return lines;
}

//#endregion
export { buildSpanTree, extractTracesFromEnvelopes, formatErrorEnvelope, formatEventOutput, formatLogEnvelope, formatTraceSummary, processErrorEvent, processLogEvent, renderSpanTree };
//# sourceMappingURL=format-BPqKr5QG.js.map